import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Admin card: tweak + preview the reminder ("nudge") email wording, and
// send a real test to the admin's own inbox to verify delivery works.
export default function NudgeEmailCard({ getAuthHeader }) {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [preview, setPreview] = useState(null);
  const [testTo, setTestTo] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const debounce = useRef(null);

  const refreshPreview = async (subj, msg) => {
    try {
      const r = await axios.post(
        `${API_URL}/api/admin/onboarding/nudge-preview`,
        { subject: subj || null, message: msg || null },
        { headers: getAuthHeader() },
      );
      setPreview(r.data);
    } catch {
      /* preview refresh is best-effort */
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const s = await axios.get(`${API_URL}/api/admin/onboarding-settings`, { headers: getAuthHeader() });
        const subj = s.data.nudge_subject || '';
        const msg = s.data.nudge_message || '';
        setSubject(subj);
        setMessage(msg);
        await refreshPreview(subj, msg);
      } catch {
        toast.error('Could not load reminder email settings');
      } finally {
        setLoaded(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!loaded) return;
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => refreshPreview(subject, message), 500);
    return () => clearTimeout(debounce.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subject, message]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/admin/onboarding/nudge-copy`,
        { subject: subject || null, message: message || null },
        { headers: getAuthHeader() },
      );
      toast.success('Reminder email wording saved — all nudges will use it');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setSending(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/admin/onboarding/nudge-test`,
        { subject: subject || null, message: message || null, to: testTo.trim() || null },
        { headers: getAuthHeader() },
      );
      toast.success(`Test email sent to ${r.data.sent_to} — check your inbox`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test send failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="wm-card p-6 rounded-lg border border-border space-y-4" data-testid="nudge-email-card">
      <div>
        <h3 className="text-lg font-semibold text-foreground">Reminder Email (Nudge)</h3>
        <p className="text-sm text-muted-foreground mt-1">
          The email stalled members receive — manually, in bulk, or from the automatic daily reminder.
          Leave a field blank to use the default wording. The member&apos;s remaining checklist steps are added automatically.
        </p>
      </div>

      {!loaded ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-foreground">Subject</Label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="You're almost set up on WeMasterminds"
                data-testid="nudge-subject-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-foreground">Message</Label>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="You're just a few steps away from getting the most out of your membership. Here's what's still waiting for you:"
                rows={5}
                data-testid="nudge-message-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-foreground">Send a test to</Label>
              <Input
                type="email"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                placeholder="Defaults to your account email"
                data-testid="nudge-test-to-input"
              />
            </div>
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={save}
                disabled={saving}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="nudge-copy-save"
              >
                {saving ? 'Saving…' : 'Save wording'}
              </Button>
              <Button
                onClick={sendTest}
                disabled={sending}
                variant="outline"
                data-testid="nudge-test-send"
              >
                {sending ? 'Sending…' : 'Send test to my inbox'}
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-foreground">Live preview</Label>
            {preview ? (
              <>
                <p className="text-xs text-muted-foreground">
                  Subject: <span className="text-foreground">{preview.subject}</span>
                </p>
                <iframe
                  title="Reminder email preview"
                  srcDoc={preview.html}
                  sandbox=""
                  className="w-full h-96 rounded-md border border-border bg-black"
                  data-testid="nudge-email-preview"
                />
              </>
            ) : (
              <p className="text-sm text-muted-foreground">Preview unavailable</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
